/* eslint-disable prettier/prettier */
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { HiOutlineUserGroup, HiPencil, HiTrash, HiUsers } from 'react-icons/hi';
import Modal from '../../components/UI/Modal/Modal';
import Pagination from '../../components/UI/Table/Pagination';
import Table from '../../components/UI/Table/Table';
import {
  SelfServedClient,
  useSelfServedClients,
} from '../../services/queries/useSelfServedClients';
import ClientUsersModal from './ClientUsersModal';
import SelfServedClientForm from './SelfServedClientForm';

const PAGE_SIZE = 20;

const SelfServedClients = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<SelfServedClient | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);

  const { data: clients = [], isLoading, error } = useSelfServedClients();

  // Filter and paginate clients
  const filtered = clients.filter(
    (client: SelfServedClient) =>
      client.name.toLowerCase().includes(search.toLowerCase()) ||
      client.description.toLowerCase().includes(search.toLowerCase())
  );
  const total = filtered.length;
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCreate = () => {
    setSelectedClient(null);
    setShowCreateModal(true);
  };

  const handleEdit = (client: SelfServedClient) => {
    setSelectedClient(client);
    setShowEditModal(true);
  };

  const handleManageUsers = (client: SelfServedClient) => {
    setSelectedClient(client);
    setShowUsersModal(true);
  };

  const handleDelete = (client: SelfServedClient) => {
    setSelectedClient(client);
    setShowDeleteModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold ${
          isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}
      >
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  return (
    <div className="p-6 bg-gray-100 min-h-[90vh] w-full">
      <div className="max-w-7xl mx-auto bg-white p-8 rounded-lg shadow flex flex-col h-[80vh]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <HiOutlineUserGroup className="text-3xl text-blue-600" />
            <h2 className="text-xl font-bold">Self-Served Clients</h2>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <input
              type="text"
              placeholder="Search by name or description..."
              className="border rounded px-4 py-2 w-full md:w-72 focus:outline-none focus:ring"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <button
              type="button"
              className="bg-blue-600 text-white px-6 py-2 rounded font-semibold hover:bg-blue-700 flex items-center gap-2"
              onClick={handleCreate}
            >
              <HiPencil className="w-4 h-4" />
              Create Client
            </button>
          </div>
        </div>

        <div className="flex flex-col flex-grow min-h-0 overflow-hidden border rounded-lg shadow-sm bg-white">
          <div className="flex-grow">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-blue-600 text-lg font-semibold">Loading...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-red-600 text-lg font-semibold">
                  Failed to load clients
                </span>
              </div>
            ) : (
              <Table className="flex-grow" dataCount={paginated.length}>
                <Table.Thead>
                  <Table.Tr className="bg-gray-50 sticky top-0 z-10 border-b">
                    <Table.Th className="py-3">Name</Table.Th>
                    <Table.Th className="py-3">Description</Table.Th>
                    <Table.Th className="py-3">Status</Table.Th>
                    <Table.Th className="py-3">Users Count</Table.Th>
                    <Table.Th className="py-3">Created</Table.Th>
                    <Table.Th className="py-3">Last Updated</Table.Th>
                    <Table.Th className="py-3">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginated.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={7} className="text-center py-6 text-gray-400">
                        No clients found.
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    paginated.map((client: SelfServedClient, idx: number) => (
                      <Table.Tr
                        key={client.id}
                        className={`border-t transition-colors ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } hover:bg-blue-50`}
                      >
                        <Table.Td className="font-medium">{client.name}</Table.Td>
                        <Table.Td className="max-w-xs truncate">
                          {client.description}
                        </Table.Td>
                        <Table.Td>{getStatusBadge(client.isActive)}</Table.Td>
                        <Table.Td className="text-center">{client.users.length}</Table.Td>
                        <Table.Td>{formatDate(client.creationTime)}</Table.Td>
                        <Table.Td>{formatDate(client.lastUpdated)}</Table.Td>
                        <Table.Td
                          className="text-right relative"
                          style={{ overflow: 'visible' }}
                        >
                          <Menu as="div" className="relative inline-block text-left">
                            <div>
                              <Menu.Button
                                className="px-2 py-1 rounded border border-gray-200 bg-white hover:bg-blue-50 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-200 transition"
                                onClick={() =>
                                  setActionMenuOpen(
                                    actionMenuOpen === client.id ? null : client.id
                                  )
                                }
                              >
                                &#x22EE;
                              </Menu.Button>
                            </div>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-2xl py-2 ring-1 ring-black/5 focus:outline-none animate-fade-in z-50">
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      className={`flex items-center w-full text-left px-4 py-2 text-gray-700 rounded transition ${
                                        active ? 'bg-blue-50 text-blue-700' : ''
                                      }`}
                                      onClick={() => handleEdit(client)}
                                    >
                                      <HiPencil className="w-4 h-4 mr-2" />
                                      Edit
                                    </button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      className={`flex items-center w-full text-left px-4 py-2 text-gray-700 rounded transition ${
                                        active ? 'bg-blue-50 text-blue-700' : ''
                                      }`}
                                      onClick={() => handleManageUsers(client)}
                                    >
                                      <HiUsers className="w-4 h-4 mr-2" />
                                      Manage Users ({client.users.length})
                                    </button>
                                  )}
                                </Menu.Item>
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      className={`flex items-center w-full text-left px-4 py-2 text-red-600 rounded transition ${
                                        active ? 'bg-red-50 text-red-700' : ''
                                      }`}
                                      onClick={() => handleDelete(client)}
                                    >
                                      <HiTrash className="w-4 h-4 mr-2" />
                                      Delete
                                    </button>
                                  )}
                                </Menu.Item>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            )}
          </div>
          <div
            className="flex items-center justify-between mt-4 text-sm text-gray-600 bg-white pt-2 z-10 sticky bottom-0 border-t border-gray-100"
            style={{ zIndex: 10 }}
          >
            <span>
              Showing {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} -{' '}
              {Math.min(page * PAGE_SIZE, total)} of {total}
            </span>
            <Pagination
              currentPage={page}
              totalCount={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* Create Client Modal */}
      <SelfServedClientForm
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        client={null}
      />

      {/* Edit Client Modal */}
      <SelfServedClientForm
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        client={selectedClient}
      />

      {/* Manage Users Modal */}
      <ClientUsersModal
        open={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        client={selectedClient}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        setIsOpen={setShowDeleteModal}
        type="small"
        height="20vh"
        showCloseIcon
        title="Delete Client?"
      >
        <div className="mt-4 mb-6 text-gray-700 text-md">
          Are you sure you want to permanently delete &quot;{selectedClient?.name}&quot;
          and all its data?
        </div>
        <div className="flex justify-center gap-4 mt-6">
          <button
            type="button"
            className="border border-blue-500 text-blue-600 px-8 py-2 rounded-lg font-semibold bg-white hover:bg-blue-50 transition"
            onClick={() => setShowDeleteModal(false)}
          >
            No, Cancel
          </button>
          <button
            type="button"
            className="bg-red-600 text-white px-8 py-2 rounded-lg font-semibold hover:bg-red-700 transition"
            onClick={() => {
              // TODO: Implement delete functionality
              setShowDeleteModal(false);
            }}
          >
            Yes, Delete
          </button>
        </div>
        <div className="text-center text-xs text-red-500 mt-2">
          This action cannot be undone.
        </div>
      </Modal>
    </div>
  );
};

export default SelfServedClients;
